import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import {
  useQuery,
  useMutation,
  useQueryClient,
  keepPreviousData,
} from '@tanstack/react-query';
import toast from 'react-hot-toast';
import {
  getCompanyEmployees,
  createCompanyEmployee,
  updateCompanyEmployee,
  deleteCompanyEmployee,
  getRoles,
} from '../api';
import { manageEmployeeTags } from '../crm/api';
import { InlineTagSelector } from '../crm/components/InlineTagSelector';
import { TagChips } from '../crm/components/TagChips';
import { patchEmployeeInCache } from '../utils/employeeCache';
import {
  Button,
  ConfirmDeleteModal,
  SearchInput,
  SkeletonText,
  CustomSelect,
  SideDrawer,
} from '@/components';
import { V2Table } from '@/common/components';
import { parseApiError } from '@/common/utils';
import { useDelayedSkeleton } from '@/common/hooks/useDelayedSkeleton';
import { useBodyScrollLock } from '@/common/hooks';
import { PlusIcon } from '../components/Icons';
import editIcon from '../assets/editIcon.png';
import trashIcon from '../assets/trashIcon.png';
import { DashboardPage } from '../components/PremiumDesign';
import { RolesTab } from '../components/RolesTab';
import { EmployeeDetailsModal } from '../components/EmployeeDetailsModal';
import { PermissionGuard } from '@/common/components';
import { usePermissions } from '@/common/hooks/usePermissions';
import { PERMISSIONS } from '@/common/config/permissions';

const EMPLOYEE_FIELD_ORDER = [
  'name',
  'phone',
  'email',
  'password',
  'confirmPassword',
  'position',
  'department',
  'metadata',
  'contact_details',
];

function focusEmployeeField(fieldErrors) {
  const keys = Object.keys(fieldErrors || {}).filter((key) => fieldErrors[key]);
  if (!keys.length) return;
  const key =
    EMPLOYEE_FIELD_ORDER.find((item) => keys.includes(item)) || keys[0];
  const targetId = key === 'contact_details' ? 'phone' : key;
  window.setTimeout(() => {
    const el = document.getElementById(targetId);
    if (!el) return;
    el.scrollIntoView({ block: 'center' });
    el.focus();
  }, 0);
}

export function EmployeesPage() {
  const queryClient = useQueryClient();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [page, setPage] = useState(1);
  const [editingEmployee, setEditingEmployee] = useState(null);
  const [employeeToDelete, setEmployeeToDelete] = useState(null);
  const [viewingEmployee, setViewingEmployee] = useState(null);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [apiErrors, setApiErrors] = useState({});
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    confirmPassword: '',
    phone: '',
    position: '',
    department: '',
    metadata: '',
    role_id: null,
  });
  const [pendingTags, setPendingTags] = useState([]);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [showCredConfirm, setShowCredConfirm] = useState(false);

  useBodyScrollLock(showCredConfirm);
  const [showPopupPassword, setShowPopupPassword] = useState(false);
  const [activeTab, setActiveTab] = useState('employees');

  const { can, isPermissionsLoaded } = usePermissions();

  useEffect(() => {
    if (
      isPermissionsLoaded &&
      activeTab === 'roles' &&
      !can(PERMISSIONS.ROLES.VIEW)
    ) {
      setActiveTab('employees');
    }
  }, [isPermissionsLoaded, activeTab, can]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchQuery);
      setPage(1);
    }, 500);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  const getCompanyId = () => {
    try {
      const storedUser = localStorage.getItem('user');
      if (storedUser) {
        const userObj = JSON.parse(storedUser);
        return userObj?.company_id || localStorage.getItem('company_id');
      }
    } catch (e) {
      console.error('Failed to parse user object from localStorage', e);
    }
    return localStorage.getItem('company_id');
  };

  const companyId = getCompanyId();

  const {
    data,
    isLoading: isInitialLoading,
    isFetching,
  } = useQuery({
    queryKey: ['companyEmployees', companyId, debouncedSearch, page],
    queryFn: () => {
      const params = { page };
      if (debouncedSearch) params.search = debouncedSearch;
      return getCompanyEmployees(companyId, params);
    },
    enabled: !!companyId,
    placeholderData: keepPreviousData,
  });

  const showDelayedSkeleton = useDelayedSkeleton(
    isFetching && data !== undefined,
    300,
    500,
  );
  const isLoading = isInitialLoading || showDelayedSkeleton;

  const { data: rolesData } = useQuery({
    queryKey: ['companyRoles', companyId],
    queryFn: () => getRoles(companyId),
    enabled: !!companyId && isModalOpen,
  });

  const roleOptions = (
    Array.isArray(rolesData) ? rolesData : rolesData?.results || []
  ).map((role) => ({
    value: role.id,
    label: role.name,
  }));

  const createMutation = useMutation({
    mutationFn: async ({ payload, tagIds }) => {
      const created = await createCompanyEmployee({ companyId, payload });
      if (!tagIds?.length) {
        return { employee: created, tagsError: null };
      }
      try {
        const assigned = await manageEmployeeTags({
          companyId,
          employeeId: created.id,
          payload: { tag_ids: tagIds },
        });
        return { employee: assigned, tagsError: null };
      } catch (tagsError) {
        return { employee: created, tagsError };
      }
    },
    onSuccess: ({ employee, tagsError }) => {
      toast.success('Employee created successfully!');
      if (tagsError) {
        const status = tagsError?.response?.status;
        if (status === 404) {
          toast.error('Employee not found.');
        } else if (status === 400) {
          const { message } = parseApiError(tagsError);
          toast.error(message || 'Invalid tag selection.');
        } else {
          const { message } = parseApiError(tagsError);
          toast.error(message || 'Failed to assign tags.');
        }
      } else if (employee) {
        patchEmployeeInCache(queryClient, companyId, {
          ...employee,
          tags: Array.isArray(employee.tags) ? employee.tags : [],
        });
      }
      setShowCredConfirm(false);
      setIsModalOpen(false);
      setPendingTags([]);
      setFormData({
        name: '',
        email: '',
        password: '',
        confirmPassword: '',
        phone: '',
        position: '',
        department: '',
        metadata: '',
        role_id: null,
      });
      queryClient.invalidateQueries(['companyEmployees', companyId]);
    },
    onError: (err) => {
      setShowCredConfirm(false);
      if (err?.response?.status >= 500) {
        toast.error('Internal Server Error. Please try again later.');
        return;
      }
      const { message, fieldErrors } = parseApiError(err);
      setApiErrors(fieldErrors);
      focusEmployeeField(fieldErrors);
      let finalMessage = message;
      const keys = Object.keys(fieldErrors);
      if (keys.length > 0) {
        const firstKey = keys[0];
        const formattedKey =
          firstKey.charAt(0).toUpperCase() + firstKey.slice(1);
        finalMessage = `${formattedKey}: ${fieldErrors[firstKey]}`;
      }
      toast.error(finalMessage || 'Failed to create employee');
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, payload }) =>
      updateCompanyEmployee({ companyId, employeeId: id, payload }),
    onSuccess: () => {
      toast.success('Employee updated successfully!');
      setShowCredConfirm(false);
      setIsModalOpen(false);
      setEditingEmployee(null);
      setPendingTags([]);
      setFormData({
        name: '',
        email: '',
        password: '',
        phone: '',
        position: '',
        department: '',
        metadata: '',
        role_id: null,
      });
      queryClient.invalidateQueries(['companyEmployees', companyId]);
    },
    onError: (err) => {
      setShowCredConfirm(false);
      if (err?.response?.status >= 500) {
        toast.error('Internal Server Error. Please try again later.');
        return;
      }
      const { message, fieldErrors } = parseApiError(err);
      setApiErrors(fieldErrors);
      focusEmployeeField(fieldErrors);
      let finalMessage = message;
      const keys = Object.keys(fieldErrors);
      if (keys.length > 0) {
        const firstKey = keys[0];
        const formattedKey =
          firstKey.charAt(0).toUpperCase() + firstKey.slice(1);
        finalMessage = `${formattedKey}: ${fieldErrors[firstKey]}`;
      }
      toast.error(finalMessage || 'Failed to update employee');
    },
  });

  const formLocked =
    showCredConfirm || createMutation.isPending || updateMutation.isPending;

  useEffect(() => {
    if (!showCredConfirm) return undefined;
    const onKeyDown = (event) => {
      if (event.key !== 'Escape') return;
      event.preventDefault();
      event.stopPropagation();
      if (createMutation.isPending || updateMutation.isPending) return;
      setShowCredConfirm(false);
    };
    document.addEventListener('keydown', onKeyDown, true);
    return () => document.removeEventListener('keydown', onKeyDown, true);
  }, [showCredConfirm, createMutation.isPending, updateMutation.isPending]);

  const deleteMutation = useMutation({
    mutationFn: (id) => {
      if (!can(PERMISSIONS.MEMBERS.DELETE)) {
        toast.error('You do not have permission to perform this action.');
        return Promise.reject(new Error('Unauthorized'));
      }
      return deleteCompanyEmployee({ companyId, employeeId: id });
    },
    onSuccess: () => {
      toast.success('Employee deleted successfully!');
      setEmployeeToDelete(null);
      queryClient.invalidateQueries(['companyEmployees', companyId]);
    },
    onError: (err) => {
      if (err?.message === 'Unauthorized') return;
      if (err?.response?.status >= 500) {
        toast.error('Internal Server Error. Please try again later.');
        return;
      }
      const { message } = parseApiError(err);
      toast.error(message || 'Failed to delete employee');
    },
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (apiErrors[name]) {
      setApiErrors((prev) => ({ ...prev, [name]: undefined }));
    }
    if ((name === 'email' || name === 'phone') && apiErrors.contact_details) {
      setApiErrors((prev) => ({ ...prev, contact_details: undefined }));
    }
  };

  const submitPayload = () => {
    if (createMutation.isPending || updateMutation.isPending) return;
    let finalMetadata = formData.metadata;
    if (typeof finalMetadata === 'object' && finalMetadata !== null) {
      finalMetadata = JSON.stringify(finalMetadata);
    } else if (typeof finalMetadata === 'string') {
      finalMetadata = finalMetadata.trim();
    }

    const payload = { ...formData, metadata: finalMetadata };

    if (editingEmployee) {
      if (!can(PERMISSIONS.MEMBERS.UPDATE)) {
        toast.error('You do not have permission to perform this action.');
        return;
      }
      updateMutation.mutate({ id: editingEmployee.id, payload });
    } else {
      if (!can(PERMISSIONS.MEMBERS.CREATE)) {
        toast.error('You do not have permission to perform this action.');
        return;
      }
      const tagIds = pendingTags
        .map((tag) => Number(tag.id))
        .filter((id) => !Number.isNaN(id));
      createMutation.mutate({ payload, tagIds });
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (createMutation.isPending || updateMutation.isPending) return;
    if ((!editingEmployee || !editingEmployee.email) && formData.email) {
      if (formData.password !== formData.confirmPassword) {
        setApiErrors((prev) => ({
          ...prev,
          confirmPassword: 'Passwords do not match',
        }));
        return;
      }
      setShowCredConfirm(true);
      return;
    }
    submitPayload();
  };

  const openEditModal = (emp) => {
    setEditingEmployee(emp);
    setApiErrors({});
    setPendingTags([]);
    setFormData({
      name: emp.name || '',
      email: emp.email || '',
      password: emp.password || '',
      confirmPassword: '',
      phone: emp.phone || '',
      position: emp.position || '',
      department: emp.department || '',
      metadata:
        emp.metadata && emp.metadata !== '{}'
          ? typeof emp.metadata === 'object'
            ? Object.keys(emp.metadata).length > 0
              ? JSON.stringify(emp.metadata)
              : ''
            : emp.metadata
          : '',
      role_id: emp.role?.id || emp.role_id || null,
    });
    setShowPassword(false);
    setShowConfirmPassword(false);
    setIsModalOpen(true);
  };

  const employees = data?.results || [];
  const employeesTotal = data?.count || 0;
  const hasActions =
    can(PERMISSIONS.MEMBERS.UPDATE) || can(PERMISSIONS.MEMBERS.DELETE);

  const employeeColumns = [
    {
      id: 'name',
      label: 'Name',
      defaultWidth: 180,
      minWidth: 140,
      resizable: true,
      skeleton: <SkeletonText width="w-28" height="h-4" />,
      cell: (emp) => (
        <span className="truncate block" title={emp.name}>
          {emp.name}
        </span>
      ),
    },
    {
      id: 'tags',
      label: 'Tags',
      defaultWidth: 240,
      minWidth: 160,
      resizable: true,
      skeleton: <SkeletonText width="w-24" height="h-5" />,
      cell: (emp) => <TagChips tags={emp.tags} />,
    },
    {
      id: 'phone',
      label: 'Mobile',
      defaultWidth: 150,
      minWidth: 120,
      resizable: true,
      skeleton: <SkeletonText width="w-24" height="h-4" />,
      cell: (emp) => (
        <span className="truncate block" title={emp.phone || '-'}>
          {emp.phone || '-'}
        </span>
      ),
    },
    {
      id: 'email',
      label: 'Email',
      defaultWidth: 220,
      minWidth: 160,
      resizable: true,
      skeleton: <SkeletonText width="w-36" height="h-4" />,
      cell: (emp) => (
        <span className="truncate block" title={emp.email || '-'}>
          {emp.email || '-'}
        </span>
      ),
    },
    {
      id: 'position',
      label: 'Position',
      defaultWidth: 140,
      minWidth: 110,
      resizable: true,
      skeleton: <SkeletonText width="w-20" height="h-4" />,
      cell: (emp) => (
        <span className="truncate block" title={emp.position || '-'}>
          {emp.position || '-'}
        </span>
      ),
    },
    {
      id: 'role',
      label: 'Role',
      defaultWidth: 140,
      minWidth: 110,
      resizable: true,
      skeleton: <SkeletonText width="w-20" height="h-4" />,
      cell: (emp) => (
        <span className="truncate block" title={emp.role?.name || '-'}>
          {emp.role?.name || '-'}
        </span>
      ),
    },
  ];

  if (hasActions) {
    employeeColumns.push({
      id: 'actions',
      label: 'Actions',
      defaultWidth: 100,
      minWidth: 88,
      resizable: false,
      align: 'center',
      stopPropagation: true,
      skeleton: <SkeletonText width="w-12" height="h-4" className="mx-auto" />,
      cell: (emp) => (
        <div className="flex items-center justify-center gap-4">
          <PermissionGuard permission={PERMISSIONS.MEMBERS.UPDATE}>
            <button
              onClick={() => openEditModal(emp)}
              className="text-text-muted hover:text-text-primary transition-colors shrink-0"
              title="Edit"
            >
              <img
                src={editIcon}
                alt=""
                className="w-5 h-5 object-contain shrink-0"
              />
            </button>
          </PermissionGuard>
          <PermissionGuard permission={PERMISSIONS.MEMBERS.DELETE}>
            <button
              onClick={() => setEmployeeToDelete(emp)}
              className="text-red-500 hover:text-red-400 transition-colors shrink-0"
              title="Delete"
              disabled={
                deleteMutation.isPending && employeeToDelete?.id === emp.id
              }
            >
              <img
                src={trashIcon}
                alt=""
                className="w-5 h-5 object-contain shrink-0"
              />
            </button>
          </PermissionGuard>
        </div>
      ),
    });
  }

  return (
    <DashboardPage className="employees-page !pt-2 sm:!pt-4 h-full flex-1 min-h-0 flex flex-col overflow-hidden">
      <div className="flex border-b border-border-default mb-2 mt-0 sm:mt-2 shrink-0">
        <button
          onClick={() => setActiveTab('employees')}
          className={`px-4 sm:px-6 py-2.5 sm:py-3 font-semibold text-sm transition-colors relative ${
            activeTab === 'employees'
              ? 'text-primary'
              : 'text-text-muted hover:text-text-primary'
          }`}
        >
          Team Members
          {activeTab === 'employees' && (
            <div className="absolute bottom-0 left-0 w-full h-0.5 bg-primary rounded-t-md" />
          )}
        </button>
        {can(PERMISSIONS.ROLES.VIEW) && (
          <button
            onClick={() => setActiveTab('roles')}
            className={`px-4 sm:px-6 py-2.5 sm:py-3 font-semibold text-sm transition-colors relative ${
              activeTab === 'roles'
                ? 'text-primary'
                : 'text-text-muted hover:text-text-primary'
            }`}
          >
            Roles & Permissions
            {activeTab === 'roles' && (
              <div className="absolute bottom-0 left-0 w-full h-0.5 bg-primary rounded-t-md" />
            )}
          </button>
        )}
      </div>

      {activeTab === 'employees' ? (
        <div className="flex flex-col gap-2 animate-in fade-in duration-300 w-full flex-1 min-h-0 overflow-hidden">
          <div className="employees-toolbar flex flex-col sm:flex-row sm:items-center gap-3 w-full shrink-0 py-[10px]">
            <SearchInput
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search by Name or Phone"
              className="w-full sm:max-w-md sm:flex-1"
              inputClassName="h-[44px]"
            />
            <PermissionGuard permission={PERMISSIONS.MEMBERS.CREATE}>
              <Button
                variant="primary"
                onClick={() => {
                  setEditingEmployee(null);
                  setApiErrors({});
                  setPendingTags([]);
                  setFormData({
                    name: '',
                    email: '',
                    password: '',
                    confirmPassword: '',
                    phone: '',
                    position: '',
                    department: '',
                    metadata: '',
                    role_id: null,
                  });
                  setShowPassword(false);
                  setShowConfirmPassword(false);
                  setIsModalOpen(true);
                }}
                className="h-[44px] px-4 w-full sm:w-auto shrink-0 sm:ml-auto"
                icon={<PlusIcon />}
              >
                <span>Create Employee</span>
              </Button>
            </PermissionGuard>
          </div>

          <div className="employees-table w-full min-w-0 flex-1 min-h-0 flex flex-col">
            <V2Table
              tight
              columns={employeeColumns}
              data={employees}
              rowKey="id"
              storageKey="employees_table_column_widths"
              loading={isLoading || !data}
              loadingRowCount={10}
              onRowClick={(emp) => {
                setViewingEmployee(emp);
                setIsViewModalOpen(true);
              }}
              empty={
                <div className="flex flex-col items-center justify-center gap-4">
                  <div className="w-16 h-16 bg-input-background border border-border-default rounded-full flex items-center justify-center">
                    <svg
                      className="w-8 h-8 opacity-50"
                      fill="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z" />
                    </svg>
                  </div>
                  <span className="text-base font-medium">
                    No employees found
                  </span>
                </div>
              }
              pagination={{
                currentPage: page,
                totalCount: employeesTotal,
                itemsPerPage: 10,
                onChange: setPage,
              }}
            />
          </div>

          <SideDrawer
            isOpen={isModalOpen}
            onClose={() => {
              if (formLocked) return;
              setIsModalOpen(false);
            }}
            closeOnOutside={!formLocked}
            width="lg"
            className={`employee-form-drawer${showCredConfirm ? ' pointer-events-none' : ''}`}
            showClose={false}
            bodyClassName="p-0 flex flex-col min-h-0 !overflow-hidden"
            headerClassName="!px-6 !py-5"
            header={
              <div className="flex items-center justify-between w-full gap-3">
                <h2 className="text-[20px] leading-[28px] font-semibold text-[#1F2241] font-sf-pro-display">
                  {editingEmployee ? 'Edit Employee' : 'Add New Employee'}
                </h2>
                <button
                  type="button"
                  onClick={() => {
                    if (formLocked) return;
                    setIsModalOpen(false);
                  }}
                  className="w-8 h-8 rounded-[8px] bg-primary text-white flex items-center justify-center hover:bg-primary-hover transition-colors focus:outline-none shrink-0"
                  disabled={formLocked}
                  aria-label="Close"
                >
                  <svg
                    className="w-5 h-5"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2.75"
                    viewBox="0 0 24 24"
                    aria-hidden="true"
                  >
                    <line x1="18" y1="6" x2="6" y2="18" />
                    <line x1="6" y1="6" x2="18" y2="18" />
                  </svg>
                </button>
              </div>
            }
          >
            <form
              id="add-employee-form"
              onSubmit={handleSubmit}
              className="flex flex-col h-full overflow-hidden"
              autoComplete="off"
            >
              <div className="flex-1 overflow-y-auto custom-scrollbar p-6 flex flex-col gap-5 bg-white">
                {/* Basic Information */}
                <div className="flex flex-col gap-3">
                  <h4 className="text-[18px] leading-[26px] font-medium text-[#50AB3D] font-sf-pro-display">
                    Basic Information
                  </h4>
                  <div className="bg-[#F5F5F5] rounded-[12px] p-4">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                      <div className="flex flex-col gap-2">
                        <label
                          htmlFor="name"
                          className="text-[14px] font-medium text-text-secondary"
                        >
                          Full Name
                        </label>
                        <input
                          type="text"
                          id="name"
                          name="name"
                          value={formData.name}
                          onChange={handleInputChange}
                          placeholder="e.g. John Doe"
                          disabled={
                            createMutation.isPending || updateMutation.isPending
                          }
                          className={`w-full h-11 px-4 rounded-xl bg-white border text-[#1F2241] text-[14px] font-medium placeholder:text-[#868686] focus:outline-none transition-colors ${apiErrors.name ? 'border-red-500' : 'border-border-default focus:border-primary'}`}
                        />
                        {apiErrors.name && (
                          <span className="text-red-500 text-xs font-medium">
                            {apiErrors.name}
                          </span>
                        )}
                      </div>

                      <div className="flex flex-col gap-2">
                        <label
                          htmlFor="phone"
                          className="text-[14px] font-medium text-text-secondary"
                        >
                          Phone Number
                        </label>
                        <input
                          type="tel"
                          id="phone"
                          name="phone"
                          value={formData.phone}
                          onChange={handleInputChange}
                          placeholder="+1 234 567 8900"
                          disabled={
                            createMutation.isPending || updateMutation.isPending
                          }
                          className={`w-full h-11 px-4 rounded-xl bg-white border text-[#1F2241] text-[14px] font-medium placeholder:text-[#868686] focus:outline-none transition-colors ${apiErrors.phone || apiErrors.contact_details ? 'border-red-500' : 'border-border-default focus:border-primary'}`}
                        />
                        {apiErrors.phone && (
                          <span className="text-red-500 text-xs font-medium">
                            {apiErrors.phone}
                          </span>
                        )}
                      </div>

                      {apiErrors.contact_details && (
                        <span className="text-red-500 text-xs font-medium sm:col-span-2 -mt-3">
                          {apiErrors.contact_details}
                        </span>
                      )}

                      <div className="flex flex-col gap-2">
                        <label
                          htmlFor="position"
                          className="text-[14px] font-medium text-text-secondary"
                        >
                          Position
                        </label>
                        <input
                          type="text"
                          id="position"
                          name="position"
                          value={formData.position}
                          onChange={handleInputChange}
                          placeholder="e.g. Software Engineer"
                          disabled={
                            createMutation.isPending || updateMutation.isPending
                          }
                          className={`w-full h-11 px-4 rounded-xl bg-white border text-[#1F2241] text-[14px] font-medium placeholder:text-[#868686] focus:outline-none transition-colors ${apiErrors.position ? 'border-red-500' : 'border-border-default focus:border-primary'}`}
                        />
                        {apiErrors.position && (
                          <span className="text-red-500 text-xs font-medium">
                            {apiErrors.position}
                          </span>
                        )}
                      </div>

                      <div className="flex flex-col gap-2">
                        <label
                          htmlFor="department"
                          className="text-[14px] font-medium text-text-secondary"
                        >
                          Department
                        </label>
                        <input
                          type="text"
                          id="department"
                          name="department"
                          value={formData.department}
                          onChange={handleInputChange}
                          placeholder="e.g. Engineering"
                          disabled={
                            createMutation.isPending || updateMutation.isPending
                          }
                          className={`w-full h-11 px-4 rounded-xl bg-white border text-[#1F2241] text-[14px] font-medium placeholder:text-[#868686] focus:outline-none transition-colors ${apiErrors.department ? 'border-red-500' : 'border-border-default focus:border-primary'}`}
                        />
                        {apiErrors.department && (
                          <span className="text-red-500 text-xs font-medium">
                            {apiErrors.department}
                          </span>
                        )}
                      </div>

                      <div className="flex flex-col gap-2 sm:col-span-2">
                        <label
                          htmlFor="metadata"
                          className="text-[14px] font-medium text-text-secondary"
                        >
                          Additional Metadata
                        </label>
                        <input
                          type="text"
                          id="metadata"
                          name="metadata"
                          value={formData.metadata}
                          onChange={handleInputChange}
                          placeholder="Any extra details"
                          disabled={
                            createMutation.isPending || updateMutation.isPending
                          }
                          className={`w-full h-11 px-4 rounded-xl bg-white border text-[#1F2241] text-[14px] font-medium placeholder:text-[#868686] focus:outline-none transition-colors ${apiErrors.metadata ? 'border-red-500' : 'border-border-default focus:border-primary'}`}
                        />
                        {apiErrors.metadata && (
                          <span className="text-red-500 text-xs font-medium">
                            {apiErrors.metadata}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Tags — same company catalog as contacts */}
                <div className="flex flex-col gap-3">
                  <h4 className="text-[18px] leading-[26px] font-medium text-[#50AB3D] font-sf-pro-display">
                    Tags
                  </h4>
                  <div className="bg-[#F5F5F5] rounded-[12px] p-4">
                    {editingEmployee ? (
                      <InlineTagSelector
                        companyId={companyId}
                        employeeId={editingEmployee.id}
                        contactTags={
                          Array.isArray(editingEmployee.tags)
                            ? editingEmployee.tags
                            : []
                        }
                        disabled={updateMutation.isPending}
                        onAssigned={(employee) => {
                          setEditingEmployee((prev) =>
                            prev && String(prev.id) === String(employee.id)
                              ? {
                                  ...prev,
                                  ...employee,
                                  tags: Array.isArray(employee.tags)
                                    ? employee.tags
                                    : [],
                                }
                              : prev,
                          );
                        }}
                      />
                    ) : (
                      <InlineTagSelector
                        companyId={companyId}
                        contactTags={pendingTags}
                        localOnly
                        onTagsChange={setPendingTags}
                        disabled={createMutation.isPending}
                      />
                    )}
                  </div>
                </div>

                {/* Role & Account */}
                <div className="flex flex-col gap-3">
                  <h4 className="text-[18px] leading-[26px] font-medium text-[#50AB3D] font-sf-pro-display">
                    Role & Account
                  </h4>
                  <div className="bg-[#F5F5F5] rounded-[12px] p-4">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                      <div className="flex flex-col gap-2 sm:col-span-2">
                        <label className="text-[14px] font-medium text-text-secondary">
                          Select Role
                        </label>
                        <CustomSelect
                          options={roleOptions}
                          value={formData.role_id}
                          onChange={(val) =>
                            setFormData((prev) => ({ ...prev, role_id: val }))
                          }
                          placeholder="Choose a role..."
                          className="w-full h-11"
                          textSizeClass="text-[14px] font-medium"
                        />
                      </div>

                      <div className="flex flex-col gap-2 sm:col-span-2">
                        <label
                          htmlFor="email"
                          className="text-[14px] font-medium text-text-secondary"
                        >
                          Email Address
                        </label>
                        <input
                          type="email"
                          id="email"
                          name="email"
                          value={formData.email}
                          onChange={handleInputChange}
                          placeholder="name@enterprise.com"
                          autoComplete="off"
                          disabled={
                            createMutation.isPending ||
                            updateMutation.isPending ||
                            (editingEmployee && editingEmployee.email)
                          }
                          className={`w-full h-11 px-4 rounded-xl bg-white border text-[#1F2241] text-[14px] font-medium placeholder:text-[#868686] focus:outline-none transition-colors ${apiErrors.email || apiErrors.contact_details ? 'border-red-500' : 'border-border-default focus:border-primary'} ${editingEmployee && editingEmployee.email ? 'opacity-60 cursor-not-allowed' : ''}`}
                        />
                        {apiErrors.email && (
                          <span className="text-red-500 text-xs font-medium">
                            {apiErrors.email}
                          </span>
                        )}
                      </div>

                      {!(editingEmployee && editingEmployee.email) && (
                        <>
                          <div className="flex flex-col gap-2">
                            <label
                              htmlFor="password"
                              className="text-[14px] font-medium text-text-secondary"
                            >
                              Password
                            </label>
                            <div className="relative">
                              <input
                                type={showPassword ? 'text' : 'password'}
                                id="password"
                                name="password"
                                value={formData.password}
                                onChange={handleInputChange}
                                placeholder="********"
                                autoComplete="new-password"
                                disabled={
                                  createMutation.isPending ||
                                  updateMutation.isPending ||
                                  (editingEmployee && editingEmployee.email)
                                }
                                className={`w-full h-11 pl-4 pr-10 rounded-xl bg-white border text-[#1F2241] text-[14px] font-medium placeholder:text-[#868686] focus:outline-none transition-colors ${apiErrors.password ? 'border-red-500' : 'border-border-default focus:border-primary'} ${editingEmployee && editingEmployee.email ? 'opacity-60 cursor-not-allowed' : ''}`}
                              />
                              <button
                                type="button"
                                onClick={() => setShowPassword(!showPassword)}
                                className="absolute right-3 top-1/2 -translate-y-1/2 text-text-muted hover:text-text-primary focus:outline-none"
                              >
                                {showPassword ? (
                                  <svg
                                    className="w-5 h-5"
                                    fill="none"
                                    viewBox="0 0 24 24"
                                    stroke="currentColor"
                                  >
                                    <path
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      strokeWidth={2}
                                      d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                                    />
                                    <path
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      strokeWidth={2}
                                      d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                                    />
                                  </svg>
                                ) : (
                                  <svg
                                    className="w-5 h-5"
                                    fill="none"
                                    viewBox="0 0 24 24"
                                    stroke="currentColor"
                                  >
                                    <path
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      strokeWidth={2}
                                      d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21"
                                    />
                                  </svg>
                                )}
                              </button>
                            </div>
                            {apiErrors.password && (
                              <span className="text-red-500 text-xs font-medium">
                                {apiErrors.password}
                              </span>
                            )}
                          </div>

                          <div className="flex flex-col gap-2">
                            <label
                              htmlFor="confirmPassword"
                              className="text-[14px] font-medium text-text-secondary"
                            >
                              Confirm Password
                            </label>
                            <div className="relative">
                              <input
                                type={showConfirmPassword ? 'text' : 'password'}
                                id="confirmPassword"
                                name="confirmPassword"
                                value={formData.confirmPassword}
                                onChange={handleInputChange}
                                placeholder="********"
                                autoComplete="new-password"
                                disabled={
                                  createMutation.isPending ||
                                  updateMutation.isPending ||
                                  (editingEmployee && editingEmployee.email)
                                }
                                className={`w-full h-11 pl-4 pr-10 rounded-xl bg-white border text-[#1F2241] text-[14px] font-medium placeholder:text-[#868686] focus:outline-none transition-colors ${apiErrors.confirmPassword ? 'border-red-500' : 'border-border-default focus:border-primary'} ${editingEmployee && editingEmployee.email ? 'opacity-60 cursor-not-allowed' : ''}`}
                              />
                              <button
                                type="button"
                                onClick={() =>
                                  setShowConfirmPassword(!showConfirmPassword)
                                }
                                className="absolute right-3 top-1/2 -translate-y-1/2 text-text-muted hover:text-text-primary focus:outline-none"
                              >
                                {showConfirmPassword ? (
                                  <svg
                                    className="w-5 h-5"
                                    fill="none"
                                    viewBox="0 0 24 24"
                                    stroke="currentColor"
                                  >
                                    <path
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      strokeWidth={2}
                                      d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                                    />
                                    <path
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      strokeWidth={2}
                                      d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                                    />
                                  </svg>
                                ) : (
                                  <svg
                                    className="w-5 h-5"
                                    fill="none"
                                    viewBox="0 0 24 24"
                                    stroke="currentColor"
                                  >
                                    <path
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      strokeWidth={2}
                                      d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21"
                                    />
                                  </svg>
                                )}
                              </button>
                            </div>
                            {apiErrors.confirmPassword && (
                              <span className="text-red-500 text-xs font-medium">
                                {apiErrors.confirmPassword}
                              </span>
                            )}
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Footer */}
              <div className="flex items-center justify-end px-6 py-5 border-t border-[#EBEBEB] shrink-0 gap-3 bg-white">
                <Button
                  type="submit"
                  variant="primary"
                  disabled={
                    createMutation.isPending || updateMutation.isPending
                  }
                  className="h-[44px] px-5 text-[15px] font-medium rounded-[8px] gap-2 min-w-[140px] flex items-center justify-center"
                >
                  {createMutation.isPending || updateMutation.isPending ? (
                    <svg
                      className="animate-spin h-4 w-4 text-white"
                      fill="none"
                      viewBox="0 0 24 24"
                    >
                      <circle
                        className="opacity-25"
                        cx="12"
                        cy="12"
                        r="10"
                        stroke="currentColor"
                        strokeWidth="4"
                      ></circle>
                      <path
                        className="opacity-75"
                        fill="currentColor"
                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                      ></path>
                    </svg>
                  ) : editingEmployee ? (
                    'Update Employee'
                  ) : (
                    'Save Employee'
                  )}
                </Button>
              </div>
            </form>
          </SideDrawer>

          {/* Delete Confirmation Modal */}
          <ConfirmDeleteModal
            isOpen={!!employeeToDelete}
            title="Delete Employee"
            itemName={employeeToDelete?.name}
            description="This user and their information will be permanently deleted."
            onConfirm={() => deleteMutation.mutate(employeeToDelete.id)}
            onCancel={() =>
              !deleteMutation.isPending && setEmployeeToDelete(null)
            }
            isPending={deleteMutation.isPending}
          />

          {/* Credentials Confirmation Modal */}
          {showCredConfirm &&
            createPortal(
              <div
                className="fixed inset-0 z-[120] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-in fade-in duration-200"
                onMouseDown={(e) => e.stopPropagation()}
                onClick={(e) => e.stopPropagation()}
              >
                <div
                  data-modal-root
                  className="w-full max-w-md bg-card-background border border-border-default rounded-2xl shadow-2xl p-6 flex flex-col gap-4 animate-in zoom-in-95 duration-200"
                >
                  <div className="flex items-center gap-3 text-primary">
                    <svg
                      className="w-6 h-6 shrink-0"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                      />
                    </svg>
                    <h3 className="text-lg font-bold text-text-primary">
                      Save Credentials
                    </h3>
                  </div>
                  <p className="text-sm text-text-secondary leading-relaxed">
                    <strong>Important:</strong> Please make sure you have
                    copied, noted down, or taken a screenshot of the email and
                    password you just entered.
                    <br />
                    <br />
                    For security reasons, the password will{' '}
                    <strong>not be shown again</strong> after you submit this
                    form.
                  </p>

                  <div className="bg-layer-5-card border border-border-default rounded-xl p-4 flex flex-col gap-3 mt-2">
                    <div className="flex flex-col gap-1">
                      <span className="text-xs font-semibold text-text-muted uppercase tracking-wider">
                        Email
                      </span>
                      <span className="text-sm font-medium text-text-primary break-all">
                        {formData.email}
                      </span>
                    </div>
                    <div className="flex flex-col gap-1">
                      <span className="text-xs font-semibold text-text-muted uppercase tracking-wider">
                        Password
                      </span>
                      <div className="flex items-center justify-between gap-3 bg-layer-6-elevated px-3 py-2 rounded-lg border border-border-default">
                        <span className="text-sm font-medium text-text-primary font-mono truncate">
                          {showPopupPassword
                            ? formData.password
                            : '•'.repeat(formData.password.length || 8)}
                        </span>
                        <div className="flex items-center gap-1">
                          <button
                            type="button"
                            onClick={() =>
                              setShowPopupPassword(!showPopupPassword)
                            }
                            className="shrink-0 p-1.5 text-text-muted hover:text-text-primary hover:bg-layer-7-hover rounded-md transition-colors"
                            title={
                              showPopupPassword
                                ? 'Hide Password'
                                : 'Show Password'
                            }
                          >
                            {showPopupPassword ? (
                              <svg
                                className="w-4 h-4"
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke="currentColor"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21"
                                />
                              </svg>
                            ) : (
                              <svg
                                className="w-4 h-4"
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke="currentColor"
                              >
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                                />
                                <path
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                  strokeWidth={2}
                                  d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                                />
                              </svg>
                            )}
                          </button>
                          <button
                            type="button"
                            onClick={() => {
                              navigator.clipboard.writeText(formData.password);
                              toast.success('Password copied to clipboard!');
                            }}
                            className="shrink-0 p-1.5 text-text-muted hover:text-text-primary hover:bg-layer-7-hover rounded-md transition-colors"
                            title="Copy Password"
                          >
                            <svg
                              className="w-4 h-4"
                              fill="none"
                              viewBox="0 0 24 24"
                              stroke="currentColor"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"
                              />
                            </svg>
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="flex justify-end gap-3 mt-4 pt-4 border-t border-border-default">
                    <button
                      type="button"
                      onClick={() => {
                        if (
                          createMutation.isPending ||
                          updateMutation.isPending
                        ) {
                          return;
                        }
                        setShowCredConfirm(false);
                      }}
                      disabled={
                        createMutation.isPending || updateMutation.isPending
                      }
                      className="px-4 py-2 text-sm font-semibold rounded-xl bg-layer-6-elevated text-text-muted hover:text-text-primary transition-colors border border-border-default"
                    >
                      Go Back
                    </button>
                    <Button
                      type="button"
                      variant="primary"
                      onClick={submitPayload}
                      disabled={
                        createMutation.isPending || updateMutation.isPending
                      }
                      className="px-5 py-2 h-[44px] text-sm font-semibold rounded-xl shadow-lg flex items-center justify-center min-w-[120px]"
                    >
                      {createMutation.isPending || updateMutation.isPending ? (
                        <svg
                          className="animate-spin h-4 w-4 text-text-primary"
                          fill="none"
                          viewBox="0 0 24 24"
                        >
                          <circle
                            className="opacity-25"
                            cx="12"
                            cy="12"
                            r="10"
                            stroke="currentColor"
                            strokeWidth="4"
                          ></circle>
                          <path
                            className="opacity-75"
                            fill="currentColor"
                            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                          ></path>
                        </svg>
                      ) : (
                        'Yes, Submit'
                      )}
                    </Button>
                  </div>
                </div>
              </div>,
              document.body,
            )}
        </div>
      ) : (
        <div className="flex flex-col w-full flex-1 min-h-0 overflow-hidden">
          <RolesTab />
        </div>
      )}

      <EmployeeDetailsModal
        isOpen={isViewModalOpen}
        onClose={() => {
          setIsViewModalOpen(false);
          setViewingEmployee(null);
        }}
        onEdit={
          can(PERMISSIONS.MEMBERS.UPDATE)
            ? (emp) => {
                setIsViewModalOpen(false);
                setViewingEmployee(null);
                openEditModal(emp);
              }
            : undefined
        }
        employeeId={viewingEmployee?.id}
        companyId={companyId}
      />
    </DashboardPage>
  );
}
